import type {MutableRefObject} from 'react';
import {useLayoutEffect, useRef} from 'react';
import {noop} from '@Utils/general.util';
import type {OnPanEnd, PanEndEvent} from '../types';

export type UsePanStrategy = (timelineRef: MutableRefObject<HTMLElement | null>, onPanEnd: OnPanEnd) => void;

export const useWheelStrategy: UsePanStrategy = (timelineRef, onPanEnd): void => {
  useLayoutEffect(() => {
    const element = timelineRef.current;

    const pointerWheelHandler = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;

      event.preventDefault();

      const isHorizontal = event.shiftKey;

      const panEndEvent: PanEndEvent = {
        clientX: event.clientX,
        clientY: event.clientY,
        deltaX: isHorizontal ? event.deltaX || event.deltaY : 0,
        deltaY: isHorizontal ? 0 : event.deltaY,
      };

      onPanEnd(panEndEvent);
    };

    element?.addEventListener('wheel', pointerWheelHandler, {passive: false});

    return () => {
      element?.removeEventListener('wheel', pointerWheelHandler);
    };
  }, [onPanEnd, timelineRef]);
};

export const useDragStrategy: UsePanStrategy = (timelineRef, onPanEnd) => {
  const lastDragX = useRef<number | null>(null);

  useLayoutEffect(() => {
    const element = timelineRef.current;
    if (!element) return noop;

    const pointerWheelHandler = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;

      event.preventDefault();

      const isHorizontal = event.shiftKey;

      const panEndEvent: PanEndEvent = {
        clientX: event.clientX,
        clientY: event.clientY,
        deltaX: 0,
        deltaY: isHorizontal ? 0 : event.deltaY,
      };

      onPanEnd(panEndEvent);
    };

    element.addEventListener('wheel', pointerWheelHandler, {passive: false});

    return () => {
      element.removeEventListener('wheel', pointerWheelHandler);
    };
  }, [onPanEnd, timelineRef]);

  useLayoutEffect(() => {
    const element = timelineRef.current;

    const pointerdownHandler = (event: MouseEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;

      lastDragX.current = event.clientX;
    };

    const pointerupHandler = (): void => {
      lastDragX.current = null;
    };

    const pointermoveHandler = (event: MouseEvent): void => {
      if (!lastDragX.current) return;

      const deltaX = event.clientX - lastDragX.current;

      lastDragX.current = event.clientX;

      const panEndEvent: PanEndEvent = {
        deltaX: -deltaX,
        deltaY: 0,
      };

      onPanEnd(panEndEvent);
    };

    element?.addEventListener('pointerup', pointerupHandler);
    element?.addEventListener('pointerdown', pointerdownHandler);
    element?.addEventListener('pointermove', pointermoveHandler);
    element?.addEventListener('pointerleave', pointerupHandler);

    return () => {
      element?.removeEventListener('pointerup', pointerupHandler);
      element?.removeEventListener('pointerdown', pointerdownHandler);
      element?.removeEventListener('pointermove', pointermoveHandler);
      element?.removeEventListener('pointerleave', pointerupHandler);
    };
  }, [onPanEnd, timelineRef]);
};
